import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { isFeatureEnabled } from '@/lib/featureFlags'

// Extend vue-router's RouteMeta
declare module 'vue-router' {
  interface RouteMeta {
    title?: string
    description?: string
    keywords?: string
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'Home',
    component: () => import('@/pages/HomePage.vue'),
    meta: {
      title: 'GreyED - We do IT right the first time | EdTech Development',
      description: 'First-class developer specializing in building edTech solutions. Expert project management, strategic planning, and technical consulting for K-12 organizations and education solution providers.',
      keywords: 'edtech, education technology, k12 software development, educational software, project management, technical consulting'
    }
  },
  {
    path: '/about',
    name: 'About',
    component: () => import('@/pages/AboutPage.vue'),
    meta: {
      title: 'About Us - GreyED | EdTech Development Experts',
      description: 'Learn about GreyED\'s origin story, our founder Dr. Julie Carter, and our mission to bridge the gap in educational technology development for K-12 organizations.',
      keywords: 'about greyed, dr julie carter, edtech experts, education technology company, k12 development team'
    }
  },
  {
    path: '/why-work-with-us',
    name: 'WhyWorkWithUs',
    component: () => import('@/pages/WhyWorkWithUsPage.vue'),
    meta: {
      title: 'Why Work With Us - GreyED | EdTech Partnership Benefits',
      description: 'Discover the GreyED difference: expert project management, cohesive teams, consistent communication, and formalized hand-offs for successful edTech projects.',
      keywords: 'edtech partnership, project management, software development team, education technology consulting'
    }
  },
  {
    path: '/services',
    name: 'Services',
    component: () => import('@/pages/ServicesPage.vue'),
    meta: {
      title: 'Services - GreyED | EdTech Development & Consulting',
      description: 'Comprehensive edTech services including vetting guidance, cohesive team building, consistent communication, expert project management, and formalized hand-offs.',
      keywords: 'edtech services, software development, project management, technical consulting, education solutions'
    }
  },
  ...(isFeatureEnabled('testimonials')
    ? [
        {
          path: '/testimonies',
          name: 'Testimonies',
          component: () => import('@/pages/TestimoniesPage.vue'),
          meta: {
            title: 'Testimonies - GreyED | Client Success Stories',
            description: 'Read testimonials from our satisfied clients and partners in the education technology industry.',
            keywords: 'client testimonials, edtech reviews, customer success stories, education technology feedback'
          }
        }
      ]
    : []),
  {
    path: '/honors-and-awards',
    name: 'HonorsAndAwards',
    component: () => import('@/pages/HonorsAndAwardsPage.vue'),
    meta: {
      title: 'Honors and Awards - GreyED | Recognition & Achievements',
      description: 'Explore GreyED\'s industry recognition including NSBA 20-to-Watch, SBA Women-Owned Small Business, WBENC certification, and speaking engagements at major edTech conferences.',
      keywords: 'edtech awards, industry recognition, nsba, wbenc, education technology achievements'
    }
  },
  {
    path: '/cookies',
    name: 'Cookies',
    component: () => import('@/pages/CookiePolicyPage.vue'),
    meta: { 
      title: 'Cookie Policy - GreyED',
      description: 'Learn about how GreyED uses cookies on our website to improve your browsing experience.'
    }
  },
  {
    path: '/privacy',
    name: 'Privacy',
    component: () => import('@/pages/PrivacyPage.vue'),
    meta: { 
      title: 'Privacy Policy - GreyED',
      description: 'Read our privacy policy to understand how GreyED collects, uses, and protects your personal information.'
    }
  },
  ...(isFeatureEnabled('termsOfService')
    ? [
        {
          path: '/terms',
          name: 'Terms',
          component: () => import('@/pages/TermsPage.vue'),
          meta: {
            title: 'Terms of Service - GreyED',
            description: 'Review the terms of service for using GreyED\'s website and services.'
          }
        }
      ]
    : []),
  ...(isFeatureEnabled('diagnostics')
    ? [
        {
          path: '/diagnostics',
          name: 'Diagnostics',
          component: () => import('@/pages/DiagnosticsPage.vue'),
          meta: {
            title: 'Diagnostics - GreyED',
            description: 'Operational diagnostics for the GreyED contact service.'
          }
        }
      ]
    : []),
  ...(isFeatureEnabled('contactForm')
    ? [
        {
          path: '/contact',
          name: 'Contact',
          component: () => import('@/pages/ContactPage.vue'),
          meta: {
            title: 'Contact Us - GreyED | Get In Touch',
            description: 'Contact GreyED for inquiries about edTech development, project management, and technical consulting services.',
            keywords: 'contact greyed, edtech consulting contact, education technology inquiry'
          }
        }
      ]
    : [])
  ,
  {
    path: '/admin',
    name: 'Admin',
    component: () => import('@/pages/AdminPage.vue'),
    meta: {
      title: 'Admin Panel - GreyED',
      description: 'Workspace-authenticated admin panel for contact submissions and logs.'
    }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    // Always scroll to top when navigating to a new page
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Update page title and meta tags on route change
router.beforeEach((to, _from, next) => {
  const meta = to.meta
  
  // Update title
  if (meta.title) {
    document.title = meta.title
  }
  
  // Update meta description
  const updateOrCreateMeta = (name: string, content: string | undefined, property = false) => {
    if (!content) return
    
    const attribute = property ? 'property' : 'name'
    let element = document.querySelector(`meta[${attribute}="${name}"]`)
    
    if (!element) {
      element = document.createElement('meta')
      element.setAttribute(attribute, name)
      document.head.appendChild(element)
    }
    
    element.setAttribute('content', content)
  }
  
  // Update canonical link
  const updateCanonical = (path: string) => {
    const url = `https://greyed.com${path}`
    let element = document.querySelector('link[rel="canonical"]')
    
    if (!element) {
      element = document.createElement('link')
      element.setAttribute('rel', 'canonical')
      document.head.appendChild(element)
    }
    
    element.setAttribute('href', url)
  }
  
  updateOrCreateMeta('description', meta.description)
  updateOrCreateMeta('keywords', meta.keywords)
  updateOrCreateMeta('og:title', meta.title, true)
  updateOrCreateMeta('og:description', meta.description, true)
  updateOrCreateMeta('og:url', `https://greyed.com${to.path}`, true)
  updateOrCreateMeta('twitter:title', meta.title, true)
  updateOrCreateMeta('twitter:description', meta.description, true)
  updateOrCreateMeta('twitter:url', `https://greyed.com${to.path}`, true)
  updateCanonical(to.path)
  
  next()
})

export default router
